/* sc-pdp-customily-autoswitch.js — auto-switch gallery to the Customily canvas slide.
 *
 * Problem: user scrolls PDP gallery to thumbnail 2/3 (non-Customily image), then picks
 * a Customily option (e.g. "5 kids"). Customily mounts its canvas inside a media
 * slide, but that slide is not guaranteed to be slide 0 on every product.
 *
 * Fix: any input/change inside `#cl_optionsapp` → call `media-gallery.setActiveMedia()`
 * with the media item that actually contains `.cl-canvas-container`. Fall back to
 * the first media only while Customily has not mounted its canvas yet.
 *
 * Debounced 150ms to avoid thrashing khi user rapid-fires multiple picks.
 *
 * 26/08/2026 (lane fable-b7) — mobile: tap dropdown nickname bị "nhảy lên đầu".
 *   (a) Click trên UI dropdown Customily (`.csh-dropdown-container` mở/đóng,
 *       `.csh-dropdown-panel`/`.csh-dropdown-filter` = portal vào <body>, gõ tìm)
 *       KHÔNG lên lịch switch — chọn 1 mục trong panel đổi canvas và được phủ bởi
 *       `customily-canvas-rendered` (giữ nguyên).
 *   (b) Bước switch trên mobile không được cuộn trang theo chiều dọc: vendor
 *       media-gallery.js `setActiveMedia` gọi `activeMedia.scrollIntoView({smooth})`
 *       trong setTimeout(0) khi không có thumbnails (mobile) → trang nhảy lên
 *       gallery, panel dropdown (toạ độ trang) rơi ra ngoài viewport. Ta bọc
 *       vendor bằng shadow `scrollIntoView` no-op trên chính slide đích trong
 *       cửa sổ gọi (bao cả lời gọi lồng của sc-pdp-mobile-gallery-fade.js qua
 *       MutationObserver is-active → syncThumbnail → setActiveMedia). Không sửa
 *       vendor; desktop (thumbnail_slider, vendor không scrollIntoView) giữ nguyên.
 */
(function () {
  'use strict';

  var debounceTimer = null;

  /* Vendor + fade script cùng dùng mốc 750px. */
  var desktopMql = window.matchMedia('(min-width: 750px)');

  /* UI dropdown Customily (Vue). `.csh-dropdown-container` nằm trong .customily_option;
   * panel/filter được portal vào <body> (ngoài #cl_optionsapp) nhưng liệt kê cho rõ. */
  var DROPDOWN_UI_SELECTOR = '.csh-dropdown-container, .csh-dropdown-input-container, ' +
    '.csh-dropdown-panel, .csh-dropdown-filter';

  /* Thời gian giữ shadow: vendor scrollIntoView chạy ở setTimeout(0); fade script gọi
   * lồng cũng setTimeout(0) từ MutationObserver (microtask). 300 ms là dư an toàn. */
  var NO_SCROLL_SHADOW_MS = 300;

  function noop() {}

  /* Gọi vendor setActiveMedia nhưng chặn cuộn dọc trang: shadow `scrollIntoView` bằng
   * own-property no-op trên slide đích, restore sau NO_SCROLL_SHADOW_MS. Gọi lặp trong
   * cửa sổ chỉ dời lịch restore (không xoá sớm). Mọi việc khác của vendor (toggle
   * is-active, scrollTo ngang khi có thumbnails, playActiveMedia, live region) giữ nguyên. */
  function setActiveMediaNoScroll(gallery, targetMedia, mediaId) {
    if (targetMedia._scNoScrollTimer) clearTimeout(targetMedia._scNoScrollTimer);
    targetMedia.scrollIntoView = noop;
    try {
      gallery.setActiveMedia(mediaId);
    } finally {
      targetMedia._scNoScrollTimer = window.setTimeout(function () {
        targetMedia._scNoScrollTimer = null;
        if (targetMedia.scrollIntoView === noop) delete targetMedia.scrollIntoView;
      }, NO_SCROLL_SHADOW_MS);
    }
  }

  function switchToCustomilyMedia() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    var activeCanvas = gallery.querySelector('.canvas-container:not(.op-zero)');
    var canvasHost = activeCanvas && activeCanvas.closest('.cl-canvas-container');
    if (!canvasHost) canvasHost = gallery.querySelector('.cl-canvas-container');
    var targetMedia = canvasHost && canvasHost.closest('[data-media-id]');
    if (!targetMedia) targetMedia = gallery.querySelector('[data-media-id]');
    if (!targetMedia) return;
    var mediaId = targetMedia.dataset.mediaId;
    if (!mediaId) return;
    try {
      if (desktopMql.matches) {
        gallery.setActiveMedia(mediaId);
      } else {
        setActiveMediaNoScroll(gallery, targetMedia, mediaId);
      }
    } catch (e) {
      /* Silent — vendor API may differ on older Shrine versions. */
    }
  }

  function scheduleSwitch() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(switchToCustomilyMedia, 150);
  }

  function isCustomilyEvent(e) {
    var t = e.target;
    if (!t || !t.closest) return false;
    /* Axe Shipping Protection checkbox được sc-axe-position.js đặt TRONG
     * #cl_optionsapp (trước preview CTA). Toggle SP không phải là đổi option
     * Customily — không được reset gallery về slide 0 (và tránh setActiveMedia
     * lúc slider chưa init → onSlideChanged đọc dataset undefined). */
    if (t.closest('[class*="axe-checkbox"]')) return false;
    return !!t.closest('#cl_optionsapp');
  }

  /* Skip text/textarea/number inputs — user tap vào để gõ, không phải pick option.
   * Vendor setActiveMedia sẽ scrollIntoView gallery (media-gallery.js:62 khi layout
   * không có thumbnails / mobile layout) → viewport scroll lên top → dismiss keyboard
   * + mất vị trí gõ. Chỉ fire cho radio/checkbox/swatch label/dropdown. */
  function isTextishInput(target) {
    if (!target) return false;
    var tag = target.tagName;
    if (tag === 'TEXTAREA') return true;
    if (tag === 'INPUT') {
      var type = (target.type || '').toLowerCase();
      return type === 'text' || type === 'number' || type === 'email' ||
             type === 'tel' || type === 'search' || type === 'url' || type === '';
    }
    /* Target cũng có thể là label/div bọc text input → check ancestor trong
     * cùng customily_option row. */
    if (target.closest) {
      var row = target.closest('.customily_option');
      if (row && row.querySelector('input[type=text], input[type=number], textarea') &&
          !row.querySelector('input[type=radio], input[type=checkbox], select, .csh-dropdown-input-container')) {
        return true;
      }
    }
    return false;
  }

  /* Click vào UI dropdown (mở/đóng panel, ô tìm, item trong panel) không phải là
   * đổi option; giá trị thật đổi qua Vue (input hidden, không bắn change) và
   * canvas render lại → `customily-canvas-rendered` lo phần switch. */
  function isDropdownUi(target) {
    return !!(target && target.closest && target.closest(DROPDOWN_UI_SELECTOR));
  }

  /* Customily swatches fire `change` on radio input + `click` on label. Text
   * `input` event bị loại khỏi trigger list. Click/change trên text input cũng
   * bị skip (tap vào để gõ, không phải pick option). */
  document.addEventListener('change', function (e) {
    if (!isCustomilyEvent(e)) return;
    if (isTextishInput(e.target)) return;
    scheduleSwitch();
  }, true);

  document.addEventListener('click', function (e) {
    if (!isCustomilyEvent(e)) return;
    if (isTextishInput(e.target)) return;
    if (isDropdownUi(e.target)) return;
    scheduleSwitch();
  }, true);

  /* The canvas may mount or move after the option click debounce. Re-resolve
   * its real media owner when Customily confirms the rendered generation. */
  window.addEventListener('customily-canvas-rendered', scheduleSwitch);
})();
